import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { CityCardProps } from './types';
import { useCitiesListContext } from '@/features/marketplace/hooks/use-get-cities';
import { useLocale } from '@/hooks/use-locale';
import './styles.css';
import { IconSaxIcon } from '@/components/design-system/atoms/icon';
import { theme } from '@/utils/util_theme';
import { navigateToCity } from '@/features/marketplace/utils/city-navigation';

// Fallback image path - using a path that can be resolved at build time
const FALLBACK_IMAGE = '/assets/images/marketplace/cities/multiple_cities.svg';

const CityCard: React.FC<CityCardProps> = ({ cityId, className, onClick }) => {
  const [isHovering, setIsHovering] = useState(false);
  const [imageError, setImageError] = useState(false);
  const { currentLocale } = useLocale();
  const { t } = useTranslation();

  const { getCityDetail } = useCitiesListContext()!;

  const cityDetail = useMemo(() => {
    return getCityDetail(cityId);
  }, [cityId, getCityDetail]);

  const cityName = useMemo(() => {
    if (cityDetail) {
      return cityDetail.name_translations[currentLocale];
    }
    return undefined;
  }, [cityDetail, currentLocale]);

  const coverImageUrl = useMemo(() => {
    if (imageError) return FALLBACK_IMAGE;
    return cityDetail?.cover_image_url || FALLBACK_IMAGE;
  }, [cityDetail?.cover_image_url, imageError]);

  const handleExploreClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent parent onClick from firing
    if (!cityDetail) return;
    navigateToCity(cityDetail, currentLocale);
  };

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <div
      className={clsx(
        'relative self-start object-cover',
        'h-[430px]',
        'w-[280px] min-w-[280px]',
        'sm:w-[364px] sm:min-w-[364px]',
        className && className
      )}
      onClick={(e) => onClick && onClick(e)}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <picture className="h-full w-full overflow-hidden object-cover">
        <img
          className={clsx('h-full w-full object-cover rounded-[24px]')}
          src={coverImageUrl}
          alt={cityName || 'City image'}
          onError={handleImageError}
          loading="lazy"
        />
      </picture>
      <div
        className={clsx(
          'city-title__container',
          'z-0',
          'rounded-b-[24px]',
          cityDetail?.regular_houses === 0 && !isHovering ? 'blur(4px) bg-E300' : '',
          isHovering && 'city-title__container--hover'
        )}
      >
        {cityDetail?.regular_houses === 0 && !isHovering ? (
          <div
            className="absolute bottom-0 left-0 flex w-full items-center justify-between px-4 min-h-[80px]"
          >
            <div className="mb-1">
              <div className="mb-1 text-[22px] font-bold text-white">{cityName}</div>
              {cityDetail?.num_houses && cityDetail.num_houses > 0 ? (
                <div className="text-[14px] font-bold text-white">
                  {cityDetail.num_houses > 1
                    ? t('js.features.marketplace.home.section2.city_card.coming_soon_houses_text', {
                        num_houses: cityDetail.num_houses,
                      })
                    : t('js.features.marketplace.home.section2.city_card.coming_soon_house_text')}
                </div>
              ) : (
                <div className="text-[14px] font-bold text-white">
                  {t('js.features.marketplace.home.section2.city_card.no_houses_text')}
                </div>
              )}
            </div>
            <span
              style={{
                background:
                  'linear-gradient(22.08deg, #F71818 -22.56%, #F9441B -9.98%, #FB7B1E 14.62%, #FEC600 58.21%, #FECA06 66.97%, #FED50E 75.17%, #FEE916 83.15%, #FFFF25 89.78%)',
              }}
              className={clsx(
                'font-sofia-pro',
                'absolute right-4 bottom-[35px] z-10',
                'rounded-full px-2 py-1',
                'text-center text-[14px] font-medium leading-[21px] text-N900'
              )}
            >
              {t('js.components.house_card.coming_soon')}
            </span>
          </div>
        ) : (
          <div
            className={clsx(
              'city-title__wrapper',
              'w-full',
              isHovering && 'city-title__wrapper--hover'
            )}
          >
            <span className={clsx('city-title', isHovering && 'city-title--hover')}>
              {cityName}
            </span>
            {cityDetail?.num_houses && cityDetail.num_houses > 0 ? (
              <span className={clsx('room-number', isHovering && 'room-number--hover')}>
                {cityDetail.num_houses > 1
                  ? t('js.features.marketplace.home.section2.city_card.houses_text', {
                      num_houses: cityDetail.num_houses,
                    })
                  : t('js.features.marketplace.home.section2.city_card.house_text')}
              </span>
            ) : (
              <div className={clsx('text-[14px] font-bold', isHovering && 'text-white')}>
                {t('js.features.marketplace.home.section2.city_card.no_houses_text')}
              </div>
            )}
          </div>
        )}
        <div className="absolute bottom-5 z-30 flex w-full justify-center">
          <a
            className={clsx(
              'explore-city__button',
              isHovering && 'explore-city__button--hover',
              'cursor-pointer'
            )}
            onClick={handleExploreClick}
          >
            <span className="text-body font-medium text-white">
              {t('js.features.marketplace.home.section2.city_card.explore_button')}
            </span>
            <IconSaxIcon name="arrow-right" strokeColor={theme?.colors?.white} />
          </a>
        </div>
      </div>
    </div>
  );
};

export { CityCard };
